;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();import PropTypes from 'prop-types';

// PropTypes for favorite items
export const FavoriteItemPropTypes = {
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['product', 'recipe']).isRequired,
    title: PropTypes.string.isRequired,
    slug: PropTypes.string,
    image: PropTypes.string,
    favoritedAt: PropTypes.instanceOf(Date).isRequired,
};

// PropTypes for FavoriteButton component
export const FavoriteButtonPropTypes = {
    itemId: PropTypes.string.isRequired,
    itemType: PropTypes.oneOf(['product', 'recipe']).isRequired,
    itemTitle: PropTypes.string.isRequired,
    itemSlug: PropTypes.string,
    itemImage: PropTypes.string,
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    variant: PropTypes.oneOf(['default', 'outline', 'minimal']),
    showCount: PropTypes.bool,
    realtime: PropTypes.bool,
    disabled: PropTypes.bool,
    className: PropTypes.string,
    onClick: PropTypes.func,
};

// Utility functions for favorites
export const favoritesUtils = {
    // Generate a unique key for localStorage
    getStorageKey: () => 'tillamook_favorites',

    // Get favorites from localStorage
    getFavoritesFromStorage: () => {
        if (typeof window === 'undefined') return [];

        try {
            const stored = localStorage.getItem(favoritesUtils.getStorageKey());
            if (!stored) return [];

            const parsed = JSON.parse(stored);
            // Convert favoritedAt back to Date objects
            return parsed
                .map(item => ({
                    ...item,
                    favoritedAt: new Date(item.favoritedAt),
                }))
                .filter(item => item.id && item.type);
        } catch (error) {
            console.error('Error reading favorites from localStorage:', error);
            return [];
        }
    },

    // Save favorites to localStorage
    saveFavoritesToStorage: favorites => {
        if (typeof window === 'undefined') return;

        try {
            localStorage.setItem(
                favoritesUtils.getStorageKey(),
                JSON.stringify(favorites)
            );
        } catch (error) {
            console.error('Error saving favorites to localStorage:', error);
        }
    },

    // Add a favorite to the list
    addFavoriteToList: (favorites, newFavorite) => {
        const exists = favorites.some(
            fav => fav.id === newFavorite.id && fav.type === newFavorite.type
        );

        if (exists) return favorites;

        return [
            ...favorites,
            {
                ...newFavorite,
                favoritedAt: new Date(),
            },
        ];
    },

    // Remove a favorite from the list
    removeFavoriteFromList: (favorites, itemId, itemType) => {
        return favorites.filter(
            fav => !(fav.id === itemId && fav.type === itemType)
        );
    },

    // Check if an item is favorited
    isFavorited: (favorites, itemId, itemType) => {
        return favorites.some(
            fav => fav.id === itemId && fav.type === itemType
        );
    },

    // Get favorites by type
    getFavoritesByType: (favorites, type) => {
        return favorites.filter(fav => fav.type === type);
    },

    // Sort favorites by date (newest first)
    sortFavoritesByDate: favorites => {
        return [...favorites].sort((a, b) => b.favoritedAt - a.favoritedAt);
    },

    // Format count in human-readable shorthand (e.g. 100, 10k, 1.2M)
    formatCount: count => {
        if (count === null || count === undefined || isNaN(count)) return '0';

        const num = Number(count);

        if (num < 1000) {
            return num.toString();
        } else if (num < 1000000) {
            const k = num / 1000;
            const decimal = k % 1;
            // Only show decimal if it's >= 0.1 (e.g. 1.1k, not 1.0k)
            return decimal >= 0.1 ? `${k.toFixed(1)}k` : `${Math.floor(k)}k`;
        } else if (num < 1000000000) {
            const m = num / 1000000;
            const decimal = m % 1;
            // Only show decimal if it's >= 0.1 (e.g. 1.1M, not 1.0M)
            return decimal >= 0.1 ? `${m.toFixed(1)}M` : `${Math.floor(m)}M`;
        } else {
            const b = num / 1000000000;
            const decimal = b % 1;
            // Only show decimal if it's >= 0.1 (e.g. 1.1B, not 1.0B)
            return decimal >= 0.1 ? `${b.toFixed(1)}B` : `${Math.floor(b)}B`;
        }
    },

    // API call to add favorite to server
    addFavoriteToServer: async (itemType, itemId) => {
        try {
            const response = await fetch('/api/favorites', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    itemType,
                    itemId,
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error adding favorite to server:', error);
            throw error;
        }
    },

    // API call to remove favorite from server
    removeFavoriteFromServer: async (itemType, itemId) => {
        try {
            const response = await fetch('/api/favorites', {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    itemType,
                    itemId,
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error removing favorite from server:', error);
            throw error;
        }
    },

    // API call to get favorite counts
    getFavoriteCounts: async items => {
        try {
            const itemsParam = items.join(',');
            const response = await fetch(
                `/api/favorites/counts?items=${encodeURIComponent(itemsParam)}`
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            return result.counts;
        } catch (error) {
            console.error('Error getting favorite counts:', error);
            throw error;
        }
    },

    // Debounce function for API calls
    debounce: (func, wait) => {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    },
};
